import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type OrmApiRepository from '@/data/repositories/ormApiRepository'
import type EntityManagerService from '@/data/repositories/entityManagerService'
import type ModelSubscriptionService from '@/services/transport/modelSubscriptionService'
import type PreloaderManager from '@/services/preloaders/preloaderManager'
import type { Preloadable } from '@/services/preloaders/types'
import type { DomainServiceSettings } from '@/decorators/types'
import { TmDomainError } from '@/core/error/tmDomainError'
import type { ModelRelationArray, RepoModel } from '@/services/domain/types'
import type BaseModel from '@/data/models/BaseModel'
import { ModelEventType } from '@/services/transport/types'
import type { LowLevelUpdateBody } from '@/services/vuex/types'
import type { IDataProvider } from '@/services/types'
import type { ModelRaw, Dict } from '@/types'

@injectable()
export default abstract class DomainBaseService<T extends OrmApiRepository> implements IDataProvider<RepoModel<T>> {
  constructor(
    @inject(SERVICE_TYPES.EntityManager) protected readonly entityManager: EntityManagerService,
    @inject(SERVICE_TYPES.ModelSubscriptionService) protected readonly subscription: ModelSubscriptionService,
    @inject(SERVICE_TYPES.PreloaderManager) protected readonly preloaderManager: PreloaderManager,
  ) {}

  /**
   * @virtual Service updates entity in the store itself — skip API reload on UPDATE model events.
   * @returns Boolean
   */
  public isSelfUpdating() {
    return false
  }

  public getDomainSettings(): DomainServiceSettings {
    throw new TmDomainError('Please use decorator @DomainSettings')
  }

  public getModel(): typeof BaseModel {
    if (!this.getDomainSettings().model) {
      throw new TmDomainError('Composite domain service must be without model')
    }

    return this.getDomainSettings().model!
  }

  public getDomainRepository(): T {
    if (!this.getDomainSettings().model) {
      throw new TmDomainError('Composite domain service must be without repository')
    }

    return this.entityManager.getRepository<T>(this.getDomainSettings().model!)
  }

  public async delete(id: string, beforeStoreUpdate?: () => Promise<any> | any) {
    await this.getDomainRepository().deleteRequest(id)
    await beforeStoreUpdate?.()
    this.deleteFromStore([id])
  }

  public deleteFromStore(ids: string[]) {
    this.getDomainRepository().delete(ids)
    this.notify(ids, ModelEventType.DELETE)
  }

  public deleteAllFromStore() {
    this.getDomainRepository().deleteAll()
    this.notify([], ModelEventType.BULK_DELETE)
  }

  public getPreloader<R extends Preloadable>(): R {
    return this.preloaderManager.getPreloader(this.getModel()) as R
  }

  protected notify(
    ids: string[],
    eventType: ModelEventType = ModelEventType.UPDATE,
    model: typeof BaseModel = this.getModel(),
  ) {
    this.subscription.emitByModel(model, { ids, eventType })
  }

  public findEntitiesByIds(ids: string[], withRelations: ModelRelationArray<RepoModel<T>> = []) {
    return this.getDomainRepository().query().with(withRelations).findIn(ids) as RepoModel<T>[]
  }

  public async getEntityById(
    id: string,
    withRelations: ModelRelationArray<RepoModel<T>> = [],
    saveFetched?: boolean,
  ): Promise<RepoModel<T> | ModelRaw<RepoModel<T>> | null> {
    const loadedEntity = this.findEntityByIdOrNull(id, withRelations)
    if (loadedEntity) {
      return loadedEntity
    }
    let fetchedEntity: ModelRaw<RepoModel<T>>
    if (this.getDomainRepository().settings().single) {
      const repo = this.getDomainRepository() as unknown as OrmApiRepository<RepoModel<T>>
      fetchedEntity = await repo.getRequest(id)
    } else {
      const res = await this.getDomainRepository().readByFilter<ModelRaw<RepoModel<T>>>([
        {
          ids: { in: [id] },
        },
      ])
      fetchedEntity = res[0]
    }

    if (!saveFetched) {
      return fetchedEntity
    }

    if (saveFetched && fetchedEntity) {
      await this.insertOrUpdate([fetchedEntity])
    }

    return this.findEntityByIdOrNull(id, withRelations)
  }

  public fill(id: string, withRelations: ModelRelationArray<RepoModel<T>> = []) {
    return this.getDomainRepository().fill(id, withRelations) as Promise<RepoModel<T>>
  }

  public async fillAll(isReset = false) {
    await this.getDomainRepository().fillAll(isReset)
    return this.findAll()
  }

  public findAll() {
    return this.getDomainRepository().all() as RepoModel<T>[]
  }

  public fetchAllItems() {
    return this.getDomainRepository().fetchAllItems() as Promise<RepoModel<T>[]>
  }

  public insertOrUpdate(payload: LowLevelUpdateBody<T extends OrmApiRepository<infer R> ? R : never>[]) {
    return this.getDomainRepository().insertOrUpdate(payload)
  }

  public insertOrUpdateItem(item: LowLevelUpdateBody<T extends OrmApiRepository<infer R> ? R : never>) {
    return this.insertOrUpdate([item])
  }

  public updateItemIfExists(item: LowLevelUpdateBody<T extends OrmApiRepository<infer R> ? R : never>) {
    return this.getDomainRepository().updateItemIfExists(item)
  }

  public isEmptyStore() {
    return this.getDomainRepository().isEmptyStore()
  }

  public findEntityByIdOrFail(t: string, withRelations?: ModelRelationArray<RepoModel<T>>): RepoModel<T> {
    return this.getDomainRepository().findEntityByIdOrFail(t, withRelations) as RepoModel<T>
  }

  public findEntityByIdOrNull(t: string, withRelations?: ModelRelationArray<RepoModel<T>>): RepoModel<T> | null {
    return this.getDomainRepository().findEntityByIdOrNull(t, withRelations) as RepoModel<T>
  }

  public findEntityOrFail(
    t: (entity: RepoModel<T>) => boolean,
    withRelations?: ModelRelationArray<RepoModel<T>>,
  ): RepoModel<T> {
    return this.getDomainRepository().findEntityOrFail(t as any, withRelations) as RepoModel<T>
  }

  public findEntityOrNull(
    t: (entity: RepoModel<T>) => boolean,
    withRelations?: ModelRelationArray<RepoModel<T>>,
  ): RepoModel<T> | null {
    return this.getDomainRepository().findEntityOrNull(t as any, withRelations) as RepoModel<T>
  }

  public firstOrFail(): RepoModel<T> {
    return this.getDomainRepository().firstOrFail() as RepoModel<T>
  }

  public firstOrNull(): RepoModel<T> | null {
    return this.getDomainRepository().firstOrNull() as RepoModel<T>
  }

  public hasItems() {
    return !!this.firstOrNull()
  }

  /**
   * Check if entity exists in store. Returns missing ids array
   *
   * @param {string[]} entityIds
   * @return {string[]}
   */
  public getMissingIds(entityIds: Array<string | `${number}` | number>): string[] {
    const strIds = entityIds.map((id) => id.toString())
    const foundIds = new Set(
      this.getDomainRepository()
        .query()
        .whereIdIn(strIds)
        .get()
        .map((entity) => `${entity.id}`),
    )

    // Using Set to remove duplicate ids from the result
    return Array.from(new Set(strIds.filter((id) => !foundIds.has(id))))
  }

  public getNonMissingIds(entityIds: Array<string | `${number}` | number>): string[] {
    const strIds = entityIds.map((id) => id.toString())
    const foundIds = new Set(
      this.getDomainRepository()
        .query()
        .whereIdIn(strIds)
        .get()
        .map((entity) => `${entity.id}`),
    )

    // Using Set to remove duplicate ids from the result
    return Array.from(new Set(strIds.filter((id) => foundIds.has(id))))
  }

  public hasAllItemsByIds(ids: Array<string | `${number}` | number>) {
    // Using Set to remove duplicate ids
    const uniqueIds = Array.from(new Set(ids.map(String)))
    if (uniqueIds.length === 0) {
      return true
    }

    return this.getMissingIds(uniqueIds).length === 0
  }

  public findOrFill(id: string, withRelations: ModelRelationArray<T> = []) {
    return this.getDomainRepository().findOrFill(id, withRelations) as Promise<RepoModel<T>>
  }

  protected addModelToStore(data: Dict<any>): Promise<RepoModel<T>> {
    return this.getDomainRepository().addModelToStore(data) as Promise<RepoModel<T>>
  }
}
